import { Box } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';

import { getPropertiesList } from 'entities/Property/model/selectors/PropertySelectors';
import {
    getRouteForbidden,
    getRouteReportsSpendSummary,
    getRouteReportsSpendSummaryCategory,
    getRouteReportsSpendSummaryProduct,
    getRouteReportsSpendSummaryProperty,
    getRouteReportsSpendSummarySupplier,
} from 'shared/const/router';
import { formatReportPercent } from 'shared/lib/reportFormatters';
import { getLastDrillCrumbLabel, withDrillTrail } from 'shared/lib/reports';
import { ReportDrillInHeader } from 'shared/ui/ReportDrillInHeader';
import {
    ReportDrillInMetrics,
    ReportDrillInSections,
} from 'shared/ui/ReportDrillInLayout';
import { ReportMetric } from 'shared/ui/ReportMetric';
import { Page } from 'widgets/Page';
import { ProductDetailsDrawer } from 'widgets/ProductDetailsDrawer';
import { navigateSpendDrillBack } from 'widgets/SpendSummaryReport/model/lib/spendDrillInNavigation';
import { mergeSpendDrillInScope } from 'widgets/SpendSummaryReport/model/types/spendDrillIn';
import { SpendDrillInBackNav } from 'widgets/SpendSummaryReport/ui/SpendDrillInBackNav';
import { SpendSummaryProductsSection } from 'widgets/SpendSummaryReport/ui/SpendSummaryProductsSection';
import { SpendSummaryPropertiesSection } from 'widgets/SpendSummaryReport/ui/SpendSummaryPropertiesSection';
import { SpendSummarySuppliersSection } from 'widgets/SpendSummaryReport/ui/SpendSummarySuppliersSection';
import { SpendSummaryTotalSpendMetric } from 'widgets/SpendSummaryReport/ui/SpendSummaryTotalSpendMetric';

import { useReportsPageSpendSummaryCategory } from '../model/hooks/useReportsPageSpendSummaryCategory';

import type { SpendProductRowDto } from 'entities/ReportSpendSummary';

const ReportsPageSpendSummaryCategory = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedProductSku, setSelectedProductSku] = useState<string | null>(
        null,
    );
    const properties = useSelector(getPropertiesList) ?? [];
    const {
        isSpendSummaryVisible,
        categoryId,
        categoryName,
        loading,
        summary,
        bySupplier,
        products,
        totalSpend,
        totalSpendPercent,
        orderCount,
        productsCount,
        dateFrom,
        dateTo,
        datePreset,
        lastRequestParamsPresent,
        locationState,
        showPropertySection,
        showSupplierSection,
    } = useReportsPageSpendSummaryCategory();

    const childScope = useMemo(
        () =>
            mergeSpendDrillInScope(locationState.scope, {
                categoryIds: categoryId ? [categoryId] : undefined,
            }),
        [categoryId, locationState.scope],
    );

    const drillParent = useMemo(
        () => ({
            path: getRouteReportsSpendSummaryCategory(categoryId ?? ''),
            label: categoryName,
        }),
        [categoryId, categoryName],
    );

    const handleBack = useCallback(() => {
        navigateSpendDrillBack(navigate, locationState.trail);
    }, [locationState.trail, navigate]);

    const handleSupplierSelect = useCallback(
        ({
            supplierId,
            supplierName,
        }: {
            supplierId: string;
            supplierName?: string;
        }) => {
            navigate(getRouteReportsSpendSummarySupplier(supplierId), {
                state: withDrillTrail(locationState, drillParent, {
                    scope: childScope,
                    contextTotalSpend: summary?.totalSpend,
                    supplierName,
                }),
            });
        },
        [childScope, drillParent, locationState, navigate, summary?.totalSpend],
    );

    const handlePropertySelect = useCallback(
        ({
            propertyId,
            propertyName,
        }: {
            propertyId: string;
            propertyName?: string;
        }) => {
            navigate(getRouteReportsSpendSummaryProperty(propertyId), {
                state: withDrillTrail(locationState, drillParent, {
                    scope: childScope,
                    contextTotalSpend: summary?.totalSpend,
                    propertyName,
                }),
            });
        },
        [childScope, drillParent, locationState, navigate, summary?.totalSpend],
    );

    const handleProductRowClick = useCallback(
        (sku: string, product: SpendProductRowDto) => {
            navigate(getRouteReportsSpendSummaryProduct(sku), {
                state: withDrillTrail(locationState, drillParent, {
                    scope: childScope,
                    contextTotalSpend: summary?.totalSpend,
                    product,
                }),
            });
        },
        [childScope, drillParent, locationState, navigate, summary?.totalSpend],
    );

    const openProductDetails = useCallback((sku: string) => {
        setSelectedProductSku(sku);
    }, []);

    const closeProductDetails = useCallback(() => {
        setSelectedProductSku(null);
    }, []);

    if (isSpendSummaryVisible === undefined) {
        return null;
    }

    if (!isSpendSummaryVisible) {
        return <Navigate to={getRouteForbidden()} replace />;
    }

    if (!categoryId || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSpendSummary()} replace />;
    }

    return (
        <Page
            dataTestId="reports-page-spend-summary-category"
            padding="24px 35px"
        >
            <Box>
                <ReportDrillInHeader
                    title={categoryName}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    datePreset={datePreset}
                    onBack={handleBack}
                    backButtonTitle={getLastDrillCrumbLabel(
                        locationState.trail,
                        t('spendSummary.reportTitle'),
                    )}
                    backButtonId="back-to-spend-summary-button"
                    backSlot={
                        <SpendDrillInBackNav
                            trail={locationState.trail}
                            onBack={handleBack}
                            id="back-to-spend-summary-button"
                        />
                    }
                />

                <ReportDrillInMetrics>
                    <SpendSummaryTotalSpendMetric
                        title={t('spendSummary.categorySpend')}
                        summary={summary}
                        primaryAmount={totalSpend}
                        loading={loading}
                        showSavingsDetail={false}
                    />
                    <ReportMetric
                        title={t('spendSummary.percentOfTotalSpend')}
                        primary={formatReportPercent(totalSpendPercent)}
                        loading={loading}
                        minHeight="100px"
                    />
                    <ReportMetric
                        title={t('Orders')}
                        primary={orderCount.toLocaleString()}
                        loading={loading}
                        minHeight="100px"
                    />
                    <ReportMetric
                        title={t('Items')}
                        primary={productsCount.toLocaleString()}
                        loading={loading}
                        minHeight="100px"
                    />
                </ReportDrillInMetrics>

                <ReportDrillInSections>
                    {showSupplierSection || showPropertySection ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '24px',
                            }}
                        >
                            {showSupplierSection ? (
                                <Box sx={{ flex: 1, minWidth: '360px' }}>
                                    <SpendSummarySuppliersSection
                                        bySupplier={bySupplier}
                                        totalSpend={summary?.totalSpend}
                                        loading={loading}
                                        onSupplierSelect={handleSupplierSelect}
                                        tableOnly
                                        tableColumnIds={[
                                            'supplier',
                                            'spend',
                                            'percentSpend',
                                        ]}
                                        showBottomBorder={false}
                                    />
                                </Box>
                            ) : null}
                            {showPropertySection ? (
                                <Box sx={{ flex: 1, minWidth: '360px' }}>
                                    <SpendSummaryPropertiesSection
                                        byProperties={
                                            summary?.byProperties ?? []
                                        }
                                        properties={properties}
                                        loading={loading}
                                        dateFrom={dateFrom}
                                        dateTo={dateTo}
                                        tableOnly
                                        hideZeroSpend
                                        tableColumnIds={[
                                            'property',
                                            'spend',
                                            'spendPerUnit',
                                            'percentSpend',
                                        ]}
                                        onPropertySelect={handlePropertySelect}
                                        showBottomBorder={false}
                                    />
                                </Box>
                            ) : null}
                        </Box>
                    ) : null}
                    <SpendSummaryProductsSection
                        products={products}
                        totalSpend={summary?.totalSpend}
                        loading={loading}
                        onProductDetailsClick={openProductDetails}
                        onOpenProductDrillIn={handleProductRowClick}
                        tableOnly
                        tableColumnIds={[
                            'item',
                            'brand',
                            'sku',
                            'properties',
                            'quantity',
                            'totalSpend',
                            'percentSpend',
                        ]}
                        showBottomBorder={false}
                    />
                    <ProductDetailsDrawer
                        sku={selectedProductSku}
                        open={Boolean(selectedProductSku)}
                        onClose={closeProductDetails}
                    />
                </ReportDrillInSections>
            </Box>
        </Page>
    );
};

export default ReportsPageSpendSummaryCategory;
